import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { ClientsBrands } from './shipment-management.service';
import { Parcels } from '../interfaces/Parcels.interfaces';
import { Damage, DamageDetail, DamageEvent, DamageAmount, DamageUserRef } from '../interfaces/Damages.interfaces';
import { Image } from '../interfaces/Image.interfaces';
import { ComParcel, ComWarehouse } from '../interfaces/ComParcel.interfaces';
import { ComParcelEvent } from '../interfaces/ComParcelEvent.interfaces';
import { Tracking } from '../interfaces/Tracking.interfaces';
import { Carriers } from '../interfaces/Carriers.interfaces';

export interface DamageWarehouseConfig {
  code: string;
  name: string;
}

export interface DamageClientConfig {
  name: string;
  code: string;
  warehouses: DamageWarehouseConfig[];
  columns: string[];
}

export interface DamageConfig {
  clients: DamageClientConfig[];
}

/** Pagina della cronologia (GET /damaged-parcels/{id}/events); `page` è 1-based */
export interface DamageEventsPage {
  events: DamageEvent[];
  total: number;
  page: number;
  size: number;
  totalPages: number;
  users: Record<string, DamageUserRef>;
}

/** Conteggi di un flusso del resoconto danni (POST /damaged-parcels/report): totale e per stato */
export interface DamageReportFlow {
  total: number;
  byStatus: { [status: string]: number };
}

/**
 * Resoconto danni del periodo (POST /damaged-parcels/report, pagina /damage-management):
 * - claims: pratiche aperte nel periodo per stato, più scadute, assegnate e chiuse nel periodo;
 * - parcels: colli con evento di danno nel periodo (con pratica aperta / chiusa / senza
 *   pratica), con immagini e numero di eventi di danno;
 * - amounts: importi creati nel periodo per stato (conteggio e somma) e per tipo (somma,
 *   esclusi gli annullati) con il totale.
 */
export interface DamageReport {
  claims: DamageReportFlow & { overdue: number; assigned: number; closedInPeriod: number };
  parcels: DamageReportFlow & { withImages: number; damageEvents: number };
  amounts: DamageReportFlow & {
    totalValue: number;
    byType: { [type: string]: number };
    valueByStatus: { [status: string]: number };
  };
}

/** Conteggi di un giorno (POST /damaged-parcels/graphics): giorno in Europe/Rome */
export interface DamageGraphicsDay {
  date: string;   // yyyy-MM-dd
  claimsOpened: number;
  claimsClosed: number;
  damagedParcels: number;
}

/** Andamento giornaliero (POST /damaged-parcels/graphics): una voce per ogni giorno dell'intervallo, anche a zero */
export interface DamageGraphicsResponse {
  days: DamageGraphicsDay[];
  totalClaimsOpened: number;
  totalClaimsClosed: number;
  totalDamagedParcels: number;
}

/** Payload di POST /damaged-parcels/{damageId}/images (sezione "Immagini" del dettaglio) */
export interface DamageImageUpload {
  fileName: string;
  mimeType: string;
  /** Contenuto Base64 senza prefisso data URL */
  contentBase64: string;
  description?: string | null;
}

export interface OprClientOption {
  clientCode: string;
  clientName: string;
}

export interface OprWarehouseOption {
  warehouseCode: string;
  warehouseName: string;
}

export interface DamageSearchRequest {
  clientCode:    string | null;
  warehouseCode: string | null;
  dateFrom:      string | null;
  dateTo:        string | null;
  page:          number;
  size:          number;
  sortField:     string;
  sortAsc:       boolean;

  // Filtri principali (allineati a shipment-management)
  parcelCode?:  string;
  waybillCode?: string;
  dealerCode?:  string;
  status?:      string;
  /**
   * Se true restituisce solo i colli con almeno una pratica danno aperta
   * (stato diverso da CLOSED e REJECTED). Vale sia per /damaged-parcels/search sia
   * per /damaged-parcels/summary. Omesso o false = tutti i colli.
   */
  onlyWithOpenClaim?: boolean;
  /**
   * Stato della pratica danno (OPEN, IN_PROGRESS, WAITING, CLOSED, REJECTED):
   * solo i colli con almeno una pratica in quello stato. Vale sia per
   * /damaged-parcels/search sia per /damaged-parcels/summary. Omesso = tutti.
   */
  claimStatus?: string;

  // Filtri avanzati (allineati a shipment-management)
  warehouseCodes?:        string[];
  carrierCodes?:          string[];
  clientCodes?:           string[];
  brandCodes?:            string[];
  zones?:                 string[];
  estimatedDeliveryDate?: string;
  actualDeliveryDate?:    string;
  companyName?:           string;
  address?:               string;
  city?:                  string;
  province?:              string;
  parcelType?:            string;
  weight?:                string;
  weightOperator?:        string;
  volume?:                string;
  volumeOperator?:        string;

  /**
   * Ricerca mirata sul singolo collo: chiede al backend di restituire il
   * collo anche se non è marcato come danneggiato (parcel_is_damaged != 'Y'),
   * così l'utente può marcarlo da UI. Il backend lo onora solo se è
   * valorizzato anche parcelCode.
   */
  includeNotDamaged?:     boolean;
}

export interface DamageRow {
  [key: string]: any;
}

export interface DamagePagedResponse {
  content:       DamageRow[];
  totalElements: number;
  totalPages?:   number;
  number?:       number;
  size?:         number;
}

/**
 * Resoconto danneggiati restituito da POST /damaged-parcels/summary:
 * colli marcati come danneggiati (parcel_is_damaged = 'Y'), quanti di
 * questi hanno almeno una pratica danno, pratiche aperte (stato diverso
 * da CLOSED/REJECTED) e pratiche chiuse con stato CLOSED.
 */
export interface DamageSummary {
  damagedParcels:          number;
  damagedParcelsWithClaim: number;
  openDamages:             number;
  closedDamages:           number;
}

/**
 * Contatori del badge della card Danni in dashboard (GET /damaged-parcels/warning-counters):
 * pratiche ancora da chiudere (stato diverso da CLOSED/REJECTED).
 */
export interface DamageWarningCounters {
  openDamagesCount: number;
}

/**
 * Dati di instradamento del collo di una pratica (GET /damaged-parcels/{damageId}/routing):
 * gli stessi di /com-parcels di shipment-management (tappe, magazzini, picking)
 * più gli eventi del collo e le anagrafiche dei vettori citati dalle tappe.
 */
export interface DamageRouting {
  comParcels:    ComParcel[];
  comWarehouses: ComWarehouse[];
  pickingEvents: Tracking[];
  events:        ComParcelEvent[];
  carriers:      Carriers[];
}

/**
 * Filtri, paginazione e ordinamento della ricerca pratiche danno
 * (POST /damaged-parcels/claims/search e /claims/summary, pagina /damage-overview).
 * Le date sono stringhe ISO; `page` è 0-based.
 */
export interface DamageClaimsSearchRequest {
  damageCode?:   string;
  parcelCode?:   string;
  waybillCode?:  string;
  statuses?:     string[];
  severities?:   string[];
  types?:        string[];
  clientCodes?:  string[];
  brandCodes?:   string[];
  carrierCodes?: string[];
  dealerCode?:   string;
  assignedTo?:   string;
  openedFrom?:   string | null;
  openedTo?:     string | null;
  onlyOverdue?:  boolean;
  page:          number;
  size:          number;
  sortField:     string;
  sortAsc:       boolean;
}

/**
 * Pagina di pratiche danno restituita da /damaged-parcels/claims/search.
 * `users` sono gli utenti citati dalle pratiche della pagina (assegnatario,
 * apertura, chiusura, audit), indicizzati per userCode, per mostrare i nomi.
 */
export interface DamageClaimsPage {
  content:       Damage[];
  users:         Record<string, DamageUserRef>;
  totalElements: number;
}

/**
 * Resoconto pratiche danno (POST /damaged-parcels/claims/summary): totale,
 * pratiche per stato e pratiche aperte con scadenza superata.
 */
export interface DamageClaimsSummary {
  total:      number;
  open:       number;
  inProgress: number;
  waiting:    number;
  closed:     number;
  rejected:   number;
  overdue:    number;
}

@Injectable({ providedIn: 'root' })
export class DamageManagementService {
  private apiUrl = `${environment.apiUrl}/damaged-parcels`;
  private token = this.authService.getAccessToken();
  private language = localStorage.getItem('language');
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.token}`,
      'Accept-language': this.language ?? 'it'
    })
  };

  constructor(private http: HttpClient, private authService: AuthService) {
  }

  /**
   * Mock di getConfiguration - prepara le strutture ma non fa richieste HTTP.
   * Può essere esteso per chiamare il backend in futuro.
   */
  getConfiguration(): Observable<DamageConfig> {
    return of({
      clients: []
    } as DamageConfig);
  }

  /**
   * Mock di getClients - prepara le strutture ma non fa richieste HTTP.
   * Può essere esteso per chiamare il backend in futuro.
   */
  getClients(): Observable<OprClientOption[]> {
    return of([] as OprClientOption[]);
  }

  /**
   * Mock di getWarehouses - prepara le strutture ma non fa richieste HTTP.
   * Può essere esteso per chiamare il backend in futuro.
   */
  getWarehouses(): Observable<OprWarehouseOption[]> {
    return of([] as OprWarehouseOption[]);
  }

  /**
   * Resoconto danneggiati (endpoint /summary) sugli stessi filtri della
   * ricerca, così le cifre corrispondono all'elenco a video. Paginazione e
   * ordinamento della richiesta vengono tolti (il backend li ignora);
   * `clientsBrands` viene inviato nel body per le autorizzazioni, come negli
   * altri service.
   */
  getSummary(request: DamageSearchRequest | null, clientsBrands: ClientsBrands[] = []): Observable<DamageSummary> {
    const { page, size, sortField, sortAsc, ...filters } = request ?? ({} as DamageSearchRequest);
    const body = { clientsBrands: clientsBrands, ...filters };
    return this.http.post<any>(`${this.apiUrl}/summary`, body, this.httpOptions)
      .pipe(
        map((res: any): DamageSummary => {
          const s = res?.summary ?? res ?? {};
          return {
            damagedParcels:          this.pickNumber(s, 'damagedParcels'),
            damagedParcelsWithClaim: this.pickNumber(s, 'damagedParcelsWithClaim'),
            openDamages:             this.pickNumber(s, 'openDamages'),
            closedDamages:           this.pickNumber(s, 'closedDamages'),
          };
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Contatori del badge della card Danni in dashboard (endpoint
   * /warning-counters, solo permesso di funzione, senza filtri cliente).
   * In caso di errore restituisce zeri così da non bloccare la dashboard,
   * come fa getWarningPendingCount di shipment-management.
   */
  getWarningCounters(): Observable<DamageWarningCounters> {
    return this.http.get<any>(`${this.apiUrl}/warning-counters`, this.httpOptions)
      .pipe(
        map((res: any): DamageWarningCounters => ({
          openDamagesCount: this.pickNumber(res ?? {}, 'openDamagesCount'),
        })),
        catchError(() => of({ openDamagesCount: 0 }))
      );
  }

  /**
   * Pratiche danno di qualunque stato (endpoint /claims/search, pagina
   * /damage-overview): filtri, ordinamento e paginazione lato backend.
   * `request.page` è già 0-based; `clientsBrands` viene inviato nel body
   * per le autorizzazioni, come negli altri service.
   *
   * Risposta backend: { damages: Opr_Damages[], users: { userCode: {...} },
   * totalCount, page, pageSize }.
   */
  searchClaims(request: DamageClaimsSearchRequest, clientsBrands: ClientsBrands[] = []): Observable<DamageClaimsPage> {
    const body = { clientsBrands: clientsBrands, ...request };
    return this.http.post<any>(`${this.apiUrl}/claims/search`, body, this.httpOptions)
      .pipe(
        map((res: any): DamageClaimsPage => ({
          content:       Array.isArray(res?.damages) ? res.damages as Damage[] : [],
          users:         res?.users && typeof res.users === 'object' ? res.users : {},
          totalElements: this.pickNumber(res ?? {}, 'totalCount')
        })),
        catchError(this.handleError)
      );
  }

  /**
   * Resoconto pratiche danno (endpoint /claims/summary) sugli stessi filtri
   * della ricerca, così le cifre corrispondono all'elenco a video. Paginazione
   * e ordinamento vengono tolti dalla richiesta (il backend li ignora).
   */
  getClaimsSummary(request: DamageClaimsSearchRequest | null, clientsBrands: ClientsBrands[] = []): Observable<DamageClaimsSummary> {
    const { page, size, sortField, sortAsc, ...filters } = request ?? ({} as DamageClaimsSearchRequest);
    const body = { clientsBrands: clientsBrands, ...filters };
    return this.http.post<any>(`${this.apiUrl}/claims/summary`, body, this.httpOptions)
      .pipe(
        map((res: any): DamageClaimsSummary => {
          const s = res?.summary ?? res ?? {};
          return {
            total:      this.pickNumber(s, 'total'),
            open:       this.pickNumber(s, 'open'),
            inProgress: this.pickNumber(s, 'inProgress'),
            waiting:    this.pickNumber(s, 'waiting'),
            closed:     this.pickNumber(s, 'closed'),
            rejected:   this.pickNumber(s, 'rejected'),
            overdue:    this.pickNumber(s, 'overdue'),
          };
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Ricerca i colli danneggiati (endpoint /search) con tutti i filtri della
   * pagina, paginazione e ordinamento lato backend. `request.page` è già
   * 0-based (il componente invia `currentPage - 1`); `clientsBrands` viene
   * inviato nel body per le autorizzazioni, come negli altri service.
   *
   * Risposta backend: { parcels: Opr_Parcels[], totalCount, page, pageSize }.
   */
  search(request: DamageSearchRequest, clientsBrands: ClientsBrands[] = []): Observable<DamagePagedResponse> {
    const body = { clientsBrands: clientsBrands, ...request };
    return this.http.post<any>(`${this.apiUrl}/search`, body, this.httpOptions)
      .pipe(
        map((res: any): DamagePagedResponse => {
          const parcels: Parcels[] = Array.isArray(res?.parcels) ? res.parcels : [];
          const totalElements = Number(res?.totalCount) || 0;
          const size = Number(res?.pageSize) || request.size || 25;
          const mapped: DamagePagedResponse = {
            content: parcels.map(p => this.mapParcelToRow(p)),
            totalElements: totalElements,
            totalPages: Math.ceil(totalElements / size),
            number: Number(res?.page) || 0,
            size: size
          };
          return mapped;
        }),
        catchError((error: any) => {
          console.error('[DamageManagementService] search() errore HTTP', error);
          return this.handleError(error);
        })
      );
  }

  /**
   * Crea (apre) una nuova pratica danno (endpoint /create). Il body è
   * direttamente la Opr_Damages_DTO: il controller la deserializza con
   * @RequestBody, quindi non vanno aggiunti campi estranei (clientsBrands).
   * Il collo viene risolto dal backend via damageParcelId o
   * damageParcelCode; damageType, damageSeverity, damageDetectedPhase e
   * damageSource sono obbligatori. Risponde 409 se il collo ha già una
   * pratica aperta.
   */
  createDamage(damage: Partial<Damage>): Observable<Damage> {
    return this.http.post<any>(`${this.apiUrl}/create`, damage, this.httpOptions)
      .pipe(
        map((res: any): Damage => res?.damage ?? res),
        catchError((error: any) => {
          // 409: il collo ha già una pratica danno aperta — messaggio del backend
          if (error?.status === 409) {
            const msg = error.error?.message || 'Il collo ha già una pratica danno aperta.';
            return throwError(() => new Error(msg));
          }
          if (error?.status === 400 && error.error?.message) {
            return throwError(() => new Error(error.error.message));
          }
          return this.handleError(error);
        })
      );
  }

  /**
   * Dettaglio completo di una pratica danno (GET /damaged-parcels/{damageId}):
   * testata, cronologia eventi, importi e totali per tipo, anagrafiche
   * collegate (collo, bolla, brand, cliente, dealer, corriere) e utenti
   * citati, per mostrare i nomi al posto dei codici. Se la pratica non
   * esiste il backend risponde 404: l'errore propagato ha name 'NotFound',
   * così il componente può distinguerlo da un errore generico.
   */
  getDamageDetail(damageId: number): Observable<DamageDetail> {
    return this.http.get<any>(`${this.apiUrl}/${damageId}`, this.httpOptions)
      .pipe(
        map((res: any): DamageDetail => ({
          damage:  res?.damage ?? null,
          events:  Array.isArray(res?.events)  ? res.events  : [],
          amounts: Array.isArray(res?.amounts) ? res.amounts : [],
          totals:  res?.totals ?? {},
          parcel:  res?.parcel  ?? null,
          waybill: res?.waybill ?? null,
          brand:   res?.brand   ?? null,
          client:  res?.client  ?? null,
          dealer:  res?.dealer  ?? null,
          carrier: res?.carrier ?? null,
          users:   res?.users && typeof res.users === 'object' ? res.users : {}
        })),
        catchError((error: any) => {
          if (error?.status === 404) {
            const notFound = new Error(error.error?.message || 'Pratica danno non trovata');
            notFound.name = 'NotFound';
            return throwError(() => notFound);
          }
          return this.handleError(error);
        })
      );
  }

  /**
   * Pagina della cronologia della pratica (GET /damaged-parcels/{damageId}/events):
   * `page` è 1-based lato componente e viene inviata 0-based; `size` è il numero di
   * eventi per pagina (max 200 lato backend). Ordine dal più vecchio, o dal più
   * recente con newestFirst. Restituisce anche gli utenti citati dagli eventi.
   */
  getDamageEvents(damageId: number, page: number, size: number, newestFirst = false): Observable<DamageEventsPage> {
    const params = new HttpParams()
      .set('page', String(Math.max(0, page - 1)))
      .set('size', String(size))
      .set('newestFirst', String(newestFirst));
    return this.http.get<any>(`${this.apiUrl}/${damageId}/events`, { ...this.httpOptions, params })
      .pipe(
        map((res: any): DamageEventsPage => ({
          events:     Array.isArray(res?.events) ? res.events : [],
          total:      Number(res?.total) || 0,
          page:       (Number(res?.page) || 0) + 1,
          size:       Number(res?.size) || size,
          totalPages: Number(res?.totalPages) || 0,
          users:      res?.users && typeof res.users === 'object' ? res.users : {}
        })),
        catchError((error: any) => {
          if (error?.status === 404 && error.error?.message) {
            return throwError(() => new Error(error.error.message));
          }
          return this.handleError(error);
        })
      );
  }

  /**
   * PDF della scheda pratica danno (GET /damaged-parcels/{damageId}/print),
   * generato dal backend con il template damages-template e la stessa catena
   * di stampa delle bolle: contiene gli stessi dati della pagina di dettaglio.
   * Restituisce il Blob del PDF, da scaricare come per le bolle (strumentario
   * di /damage-overview/detail/{id}).
   */
  printDamageReport(damageId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${damageId}/print`, {
      headers: this.httpOptions.headers,
      responseType: 'blob' as const
    }).pipe(
      catchError((error: any) => {
        if (error?.status === 404) {
          return throwError(() => new Error('Pratica danno non trovata'));
        }
        return this.handleError(error);
      })
    );
  }

  /**
   * Marca un collo come danneggiato (endpoint /mark-damaged): imposta
   * parcel_is_damaged = 'Y'. Il collo viene risolto dal backend via parcelId
   * o parcelCode; l'operazione è idempotente. Risponde 400 se il collo non
   * esiste. Il body è il solo identificativo del collo: il controller lo
   * legge come mappa, quindi non vanno aggiunti campi estranei (clientsBrands).
   */
  markParcelAsDamaged(parcelId: number | null, parcelCode: string): Observable<Parcels> {
    const body: { parcelId?: number; parcelCode: string } = { parcelCode: parcelCode };
    if (parcelId != null) body.parcelId = parcelId;
    return this.http.post<any>(`${this.apiUrl}/mark-damaged`, body, this.httpOptions)
      .pipe(
        map((res: any): Parcels => res?.parcel ?? res),
        catchError((error: any) => {
          if (error?.status === 400 && error.error?.message) {
            return throwError(() => new Error(error.error.message));
          }
          return this.handleError(error);
        })
      );
  }

  /**
   * Pratiche assegnate all'utente collegato (GET /damaged-parcels/claims/assigned, pagina
   * /damage-overview/assigned): il backend risolve l'utente dal token e risponde
   * con il suo codice e le pratiche, solo aperte oppure anche chiuse e
   * respinte con includeClosed. Ordinate dalla più recente.
   */
  getAssignedDamages(includeClosed: boolean): Observable<{ userCode: string; damages: Damage[] }> {
    const options = { ...this.httpOptions, params: { includeClosed: String(includeClosed) } };
    return this.http.get<any>(`${this.apiUrl}/claims/assigned`, options)
      .pipe(
        map((res: any) => ({
          userCode: res?.userCode ?? '',
          damages:  Array.isArray(res?.damages) ? res.damages as Damage[] : []
        })),
        catchError((error: any) => {
          if ([400, 403].includes(error?.status) && error.error?.message) {
            return throwError(() => new Error(error.error.message));
          }
          return this.handleError(error);
        })
      );
  }

  /**
   * Numero di pratiche aperte assegnate all'utente collegato (GET
   * /damaged-parcels/claims/assigned/count): badge del bottone "assegnate a me" nell'header
   * di /damaged-parcels. Il backend risolve l'utente dal token e risponde anche con
   * il suo codice.
   */
  getAssignedDamagesCount(): Observable<{ userCode: string; count: number }> {
    return this.http.get<any>(`${this.apiUrl}/claims/assigned/count`, this.httpOptions)
      .pipe(
        map((res: any) => ({
          userCode: res?.userCode ?? '',
          count:    Number(res?.count) || 0
        })),
        catchError((error: any) => this.handleError(error))
      );
  }

  /**
   * Immagini e documenti del collo della pratica (GET /damaged-parcels/{damageId}/images,
   * sezione "Immagini" di /damage-overview/detail), dalla più recente. 404 se la
   * pratica non esiste: l'errore propagato ha il messaggio del backend.
   */
  getDamageImages(damageId: number): Observable<Image[]> {
    return this.http.get<any>(`${this.apiUrl}/${damageId}/images`, this.httpOptions)
      .pipe(
        map((res: any): Image[] => Array.isArray(res?.images) ? res.images : (Array.isArray(res) ? res : [])),
        catchError((error: any) => {
          if (error?.status === 404 && error.error?.message) {
            return throwError(() => new Error(error.error.message));
          }
          return this.handleError(error);
        })
      );
  }

  /**
   * Dati di instradamento del collo della pratica (sezione "Instradamento"
   * di /damage-overview/detail). 404 se la pratica non esiste: l'errore propagato ha
   * il messaggio del backend.
   */
  getDamageRouting(damageId: number): Observable<DamageRouting> {
    const list = <T>(value: unknown): T[] => Array.isArray(value) ? value as T[] : [];
    return this.http.get<any>(`${this.apiUrl}/${damageId}/routing`, this.httpOptions)
      .pipe(
        map((res: any): DamageRouting => ({
          comParcels:    list<ComParcel>(res?.comParcels),
          comWarehouses: list<ComWarehouse>(res?.comWarehouses),
          pickingEvents: list<Tracking>(res?.pickingEvents),
          events:        list<ComParcelEvent>(res?.events),
          carriers:      list<Carriers>(res?.carriers)
        })),
        catchError((error: any) => {
          if (error?.status === 404 && error.error?.message) {
            return throwError(() => new Error(error.error.message));
          }
          return this.handleError(error);
        })
      );
  }

  /**
   * Carica un'immagine (o un PDF) dal portale sul collo della pratica
   * (POST /damaged-parcels/{damageId}/images): Opr_Images con tipo DAMAGE, sorgente
   * PORTAL, stato ACTIVE. contentBase64 senza prefisso data URL; max 10 MB.
   * Errori 400 propagano il messaggio del backend.
   */
  uploadDamageImage(damageId: number, upload: DamageImageUpload): Observable<Image> {
    return this.postOperation<Image>(`${this.apiUrl}/${damageId}/images`, upload, 'image');
  }

  // ─── RESOCONTO (pagina /damage-management) ───

  /**
   * Resoconto danni del periodo (POST /damaged-parcels/report): date facoltative,
   * senza date il resoconto copre tutti i dati. clientsBrands serve per le
   * autorizzazioni cliente/marchio. Un flusso mancante nella risposta resta a zero.
   */
  getReport(clientsBrands: ClientsBrands[], dateFrom?: string | null, dateTo?: string | null): Observable<DamageReport> {
    const body = { clientsBrands, dateFrom: dateFrom || null, dateTo: dateTo || null };
    return this.http.post<any>(`${this.apiUrl}/report`, body, this.httpOptions)
      .pipe(
        map((res: any): DamageReport => this.normalizeReport(res?.report ?? res)),
        catchError((error: any) => this.handleError(error))
      );
  }

  /**
   * Andamento giornaliero (POST /damaged-parcels/graphics): date obbligatorie
   * (estremi della finestra visualizzata), una voce per ogni giorno dell'intervallo.
   */
  getGraphics(clientsBrands: ClientsBrands[], dateFrom: string, dateTo: string): Observable<DamageGraphicsResponse> {
    const body = { clientsBrands, dateFrom, dateTo };
    return this.http.post<any>(`${this.apiUrl}/graphics`, body, this.httpOptions)
      .pipe(
        map((res: any): DamageGraphicsResponse => ({
          days: Array.isArray(res?.days) ? res.days : [],
          totalClaimsOpened:   Number(res?.totalClaimsOpened) || 0,
          totalClaimsClosed:   Number(res?.totalClaimsClosed) || 0,
          totalDamagedParcels: Number(res?.totalDamagedParcels) || 0
        })),
        catchError((error: any) => this.handleError(error))
      );
  }

  /** Resoconto con tutti i campi presenti e numerici, qualunque cosa manchi nella risposta */
  private normalizeReport(raw: any): DamageReport {
    const num = (value: unknown) => Number(value) || 0;
    const record = (value: unknown): { [key: string]: number } => {
      const result: { [key: string]: number } = {};
      if (value && typeof value === 'object') {
        Object.entries(value as object).forEach(([key, v]) => { result[key] = num(v); });
      }
      return result;
    };
    const flow = (f: any): DamageReportFlow => ({ total: num(f?.total), byStatus: record(f?.byStatus) });
    return {
      claims: {
        ...flow(raw?.claims),
        overdue:        num(raw?.claims?.overdue),
        assigned:       num(raw?.claims?.assigned),
        closedInPeriod: num(raw?.claims?.closedInPeriod)
      },
      parcels: {
        ...flow(raw?.parcels),
        withImages:   num(raw?.parcels?.withImages),
        damageEvents: num(raw?.parcels?.damageEvents)
      },
      amounts: {
        ...flow(raw?.amounts),
        totalValue:    num(raw?.amounts?.totalValue),
        byType:        record(raw?.amounts?.byType),
        valueByStatus: record(raw?.amounts?.valueByStatus)
      }
    };
  }

  // ─── OPERAZIONI SULLA PRATICA (sezione "Operazioni" di /damage-overview/detail) ───
  // Tutte POST con il solo payload dell'operazione (niente clientsBrands: il
  // controller legge mappa o DTO). Errori 400/404/409 propagano il messaggio
  // del backend (es. "La pratica ... è già chiusa") così la UI lo mostra.

  /** Cambio stato di lavorazione (/{id}/status): OPEN, IN_PROGRESS, WAITING. Per chiudere usare closeDamage. */
  changeDamageStatus(damageId: number, status: string, note?: string | null): Observable<Damage> {
    return this.postOperation<Damage>(`${this.apiUrl}/${damageId}/status`, { status, note: note || null }, 'damage');
  }

  /**
   * Modifica della classificazione (/{id}/classification): tipo danno, gravità e
   * priorità; i campi null restano invariati. Il backend registra un evento
   * CLASSIFICATION_CHANGED con il prima/dopo di ogni campo cambiato e rifiuta
   * (400) una richiesta che non cambia nulla.
   */
  updateDamageClassification(damageId: number, changes: { type?: string | null; severity?: string | null; priority?: number | null; note?: string | null }): Observable<Damage> {
    return this.postOperation<Damage>(`${this.apiUrl}/${damageId}/classification`, {
      type:     changes.type     || null,
      severity: changes.severity || null,
      priority: changes.priority ?? null,
      note:     changes.note     || null
    }, 'damage');
  }

  /** Assegnazione della pratica a un operatore (/{id}/assign) */
  assignDamage(damageId: number, assignedTo: string): Observable<Damage> {
    return this.postOperation<Damage>(`${this.apiUrl}/${damageId}/assign`, { assignedTo }, 'damage');
  }

  /**
   * Registra il magazzino in cui è avvenuto il danno sugli eventi DAMAGE del collo della
   * pratica (/{id}/damage-warehouse): parcelEventComplementaryWarehouseFrom = codice magazzino.
   * eventIds limita l'aggiornamento a quegli eventi (vuoto = tutti gli eventi DAMAGE del collo).
   * La pratica non cambia. Usato dalla sezione "Instradamento" quando il danno non è
   * collocabile sul percorso.
   */
  setDamageWarehouse(damageId: number, warehouseCode: string, eventIds: number[] = []): Observable<ComParcelEvent[]> {
    return this.postOperation<ComParcelEvent[]>(`${this.apiUrl}/${damageId}/damage-warehouse`,
      { warehouseCode, eventIds }, 'events');
  }

  /** Chiusura della pratica (/{id}/close) con stato CLOSED o REJECTED, esito e nota */
  closeDamage(damageId: number, status: string, outcome?: string | null, note?: string | null): Observable<Damage> {
    return this.postOperation<Damage>(`${this.apiUrl}/${damageId}/close`,
      { status, outcome: outcome || null, note: note || null }, 'damage');
  }

  /** Aggiunge una nota alla cronologia (/{id}/events, evento NOTE_ADDED) */
  addDamageNote(damageId: number, note: string): Observable<DamageEvent> {
    return this.postOperation<DamageEvent>(`${this.apiUrl}/${damageId}/events`,
      { damageEventType: 'NOTE_ADDED', damageEventNote: note }, 'event');
  }

  /** Aggiunge un importo alla pratica (/{id}/amounts); tipo e valore obbligatori, default EUR / PROPOSED */
  addDamageAmount(damageId: number, amount: Partial<DamageAmount>): Observable<DamageAmount> {
    return this.postOperation<DamageAmount>(`${this.apiUrl}/${damageId}/amounts`, amount, 'amount');
  }

  /** Cambia lo stato di un importo (/amounts/{amountId}/status), con eventuale id addebito collegato */
  updateDamageAmountStatus(amountId: number, status: string, chargeId?: number | null): Observable<DamageAmount> {
    return this.postOperation<DamageAmount>(`${this.apiUrl}/amounts/${amountId}/status`,
      { status, chargeId: chargeId ?? null }, 'amount');
  }

  /**
   * Modifica i dati di un importo (/amounts/{amountId}/update): tipo, valore, valuta,
   * controparte, codice controparte e nota dell'importo. I campi undefined restano
   * invariati, una stringa vuota svuota il campo; `note` è il motivo registrato
   * sull'evento AMOUNT_UPDATED. Il backend rifiuta (400) una richiesta che non cambia nulla.
   */
  updateDamageAmount(amountId: number, changes: {
    type?: string; value?: number; currency?: string;
    counterpart?: string; counterpartCode?: string; amountNote?: string;
    status?: string; chargeId?: number | null; note?: string | null;
  }): Observable<DamageAmount> {
    const body: Record<string, unknown> = {};
    if (changes.status !== undefined)          body['status'] = changes.status;
    if (changes.chargeId != null)              body['chargeId'] = changes.chargeId;
    if (changes.type !== undefined)            body['type'] = changes.type;
    if (changes.value !== undefined)           body['value'] = changes.value;
    if (changes.currency !== undefined)        body['currency'] = changes.currency;
    if (changes.counterpart !== undefined)     body['counterpart'] = changes.counterpart;
    if (changes.counterpartCode !== undefined) body['counterpartCode'] = changes.counterpartCode;
    if (changes.amountNote !== undefined)      body['amountNote'] = changes.amountNote;
    body['note'] = changes.note || null;
    return this.postOperation<DamageAmount>(`${this.apiUrl}/amounts/${amountId}/update`, body, 'amount');
  }

  /** Elimina un importo (/amounts/{amountId}/delete); il backend registra l'evento AMOUNT_DELETED con i dati rimossi */
  deleteDamageAmount(amountId: number, note?: string | null): Observable<{ damageId: number; amountId: number }> {
    return this.postOperation<{ damageId: number; amountId: number }>(
      `${this.apiUrl}/amounts/${amountId}/delete`, { note: note || null }, 'result');
  }

  private postOperation<T>(url: string, body: unknown, resultKey: string): Observable<T> {
    return this.http.post<any>(url, body, this.httpOptions)
      .pipe(
        map((res: any): T => (res?.[resultKey] ?? res) as T),
        catchError((error: any) => {
          if ([400, 404, 409].includes(error?.status) && error.error?.message) {
            return throwError(() => new Error(error.error.message));
          }
          return this.handleError(error);
        })
      );
  }

  /**
   * Converte una riga Opr_Parcels del backend nelle chiavi colonna della
   * tabella (COLUMN_MAP del componente). Il collo originale resta
   * disponibile in `parcel` per usi futuri (dettaglio, azioni);
   * `isDamaged` è il flag parcel_is_damaged normalizzato a boolean: è false
   * solo per i colli restituiti da una ricerca mirata con includeNotDamaged.
   */
  private mapParcelToRow(p: Parcels): DamageRow {
    return {
      parcelCode:  p?.parcelCode ?? null,
      waybillCode: p?.parcelWaybillCode ?? null,
      date:        p?.parcelDate ?? null,
      quantity:    p?.parcelWeight ?? null,
      volume:      p?.parcelVolume ?? null,
      // Il nome cliente (chiave colonna clientName) lo risolve il componente
      clientCode:  p?.parcelClientCode ?? null,
      damageClaim: p?.damageClaim ?? null,
      damageClaims: Array.isArray(p?.damageClaims) ? p.damageClaims : [],
      isDamaged:   DamageManagementService.isParcelDamaged(p),
      parcel:      p
    };
  }

  /**
   * Normalizza parcelIsDamaged: il DTO lo serializza come boolean, ma il
   * campo è un 'Y'/'N' su Oracle e in altri punti del frontend arriva come
   * stringa. Qualunque altro valore (null incluso) vale "non danneggiato".
   */
  static isParcelDamaged(p: Parcels | null | undefined): boolean {
    const v = p?.parcelIsDamaged;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') return v.trim().toUpperCase() === 'Y' || v.trim().toLowerCase() === 'true';
    return false;
  }

  /**
   * Estrae un valore numerico dalla mappa di risposta in modo case-insensitive
   * (le query native Oracle possono restituire gli alias in maiuscolo).
   */
  private pickNumber(res: any, key: string): number {
    if (!res) return 0;
    if (res[key] != null) return Number(res[key]) || 0;
    const lowerKey = key.toLowerCase();
    const foundKey = Object.keys(res).find(k => k.toLowerCase() === lowerKey);
    return foundKey != null ? (Number(res[foundKey]) || 0) : 0;
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'Si è verificato un errore sconosciuto';
    if (error.error instanceof ErrorEvent) {
      // Errore lato client
      errorMessage = `Errore: ${error.error.message}`;
    } else {
      // Errore lato server
      switch (error.status) {
        case 400:
          errorMessage = 'Richiesta non valida';
          break;
        case 401:
          errorMessage = 'Non autorizzato';
          break;
        case 403:
          errorMessage = 'Accesso negato';
          break;
        case 404:
          errorMessage = 'Risorsa non trovata';
          break;
        case 422:
          errorMessage = error.error?.message || 'Dati non validi';
          break;
        case 500:
          errorMessage = 'Errore interno del server';
          break;
        default:
          errorMessage = `Errore ${error.status}: ${error.error?.message || error.statusText}`;
      }
    }

    console.error('Errore HTTP:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}
